import React, { useState } from 'react';
import { useAuth } from '../Auth/AuthContext';
import APIKeyManagement from '../Users/APIKeyManagement';
import { hashPassword } from '../../utils/security';
import { apiCall } from '../../utils/apiUtils';
import { checkPasswordStrength } from '../../utils/passwordStrength';
import PasswordStrengthChecker from './PasswordStrengthChecker';

const ProfileModal = ({ onClose }) => {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState('details');
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPwFields, setShowPwFields] = useState(false);
    const [pwLoading, setPwLoading] = useState(false);
    const [pwError, setPwError] = useState('');
    const [pwSuccess, setPwSuccess] = useState('');

    const handleChangePassword = async (e) => {
        e.preventDefault();
        setPwError('');
        setPwSuccess('');

        if (newPassword !== confirmPassword) {
            setPwError('New passwords do not match');
            return;
        }
        if (!checkPasswordStrength(newPassword).isValid) {
            setPwError('New password does not meet all complexity requirements');
            return;
        }

        setPwLoading(true);
        try {
            const hashedCurrent = await hashPassword(currentPassword);
            const hashedNew = await hashPassword(newPassword);

            await apiCall('/api/auth/change-password', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    current_password: hashedCurrent,
                    new_password: hashedNew,
                    password_raw: newPassword
                })
            });

            setPwSuccess('Password changed successfully');
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (err) {
            setPwError(err.message || 'Failed to change password');
        } finally {
            setPwLoading(false);
        }
    };

    if (!user) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 overflow-hidden flex flex-col max-h-[90vh]">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-6 text-white flex justify-between items-start">
                    <div className="flex items-center">
                        <div className="w-16 h-16 rounded-full bg-white bg-opacity-20 flex items-center justify-center text-2xl font-bold border-2 border-white border-opacity-30">
                            {(user.name || user.fullName || '?').charAt(0).toUpperCase()}
                        </div>
                        <div className="ml-4">
                            <h2 className="text-xl font-bold">{user.name || user.fullName}</h2>
                            <p className="text-blue-100 text-sm">{user.email}</p>
                            <span className="inline-block mt-2 px-2 py-0.5 rounded-full bg-white bg-opacity-20 text-xs font-medium uppercase tracking-wider">
                                {user.role}
                            </span>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-white hover:text-gray-200 transition-colors bg-white bg-opacity-10 hover:bg-opacity-20 rounded-full p-1"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-gray-200">
                    <button
                        className={`flex-1 py-3 text-sm font-medium transition-colors ${activeTab === 'details'
                                ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                            }`}
                        onClick={() => setActiveTab('details')}
                    >
                        Profile Details
                    </button>
                    {user.role === 'client' && user.api_delivery_enabled && (
                        <button
                            className={`flex-1 py-3 text-sm font-medium transition-colors ${activeTab === 'docustamp'
                                    ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                                }`}
                            onClick={() => setActiveTab('docustamp')}
                        >
                            Docu Stamp API
                        </button>
                    )}
                    {user.role !== 'admin' && (
                        <button
                            className={`flex-1 py-3 text-sm font-medium transition-colors ${activeTab === 'security'
                                    ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                                }`}
                            onClick={() => setActiveTab('security')}
                        >
                            Security
                        </button>
                    )}
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto flex-1">
                    {activeTab === 'details' && (
                        <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Full Name</label>
                                    <div className="text-gray-900 font-medium">{user.name || user.fullName}</div>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Email Address</label>
                                    <div className="text-gray-900 font-medium">{user.email}</div>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Phone Number</label>
                                    <div className="text-gray-900 font-medium">{user.phone || user.mobile || user.contactNumber || '-'}</div>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Address</label>
                                    <div className="text-gray-900 font-medium">{user.address || '-'}</div>
                                </div>
                                {user.company && (
                                    <div>
                                        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Company</label>
                                        <div className="text-gray-900 font-medium">{user.company}</div>
                                    </div>
                                )}
                            </div>

                            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 mt-6">
                                <h4 className="text-sm font-semibold text-gray-900 mb-2">Account Status</h4>
                                <div className="flex items-center space-x-4">
                                    <div className="flex items-center">
                                        <div className="w-2 h-2 rounded-full bg-green-500 mr-2"></div>
                                        <span className="text-sm text-gray-600">Active</span>
                                    </div>
                                    <div className="flex items-center">
                                        <div className="w-2 h-2 rounded-full bg-blue-500 mr-2"></div>
                                        <span className="text-sm text-gray-600">Verified</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'docustamp' && (
                        <div>
                            <div className="mb-6">
                                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                                    <span className="text-2xl mr-2">🧩</span> Docu Stamp API Service
                                </h3>
                                <p className="text-sm text-gray-600 mt-1">
                                    Manage your API keys for automated document retrieval.
                                </p>
                            </div>
                            <APIKeyManagement userId={user.id} userName={user.name} />
                        </div>
                    )}

                    {activeTab === 'security' && (
                        <div>
                            <div className="mb-6">
                                <h3 className="text-lg font-semibold text-gray-900">Change Password</h3>
                                <p className="text-sm text-gray-600 mt-1">
                                    Update your account password. You will need your current password to make this change.
                                </p>
                            </div>

                            {pwSuccess && (
                                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4 text-sm" role="alert">
                                    {pwSuccess}
                                </div>
                            )}
                            {pwError && (
                                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 text-sm" role="alert">
                                    {pwError}
                                </div>
                            )}

                            <form onSubmit={handleChangePassword} className="space-y-4">
                                {/* Current Password */}
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                                        Current Password
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showPwFields ? 'text' : 'password'}
                                            value={currentPassword}
                                            onChange={(e) => setCurrentPassword(e.target.value)}
                                            required
                                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 transition duration-200 pr-10"
                                            placeholder="Enter current password"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPwFields(!showPwFields)}
                                            className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                                        >
                                            {showPwFields ? (
                                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                                                </svg>
                                            ) : (
                                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                </svg>
                                            )}
                                        </button>
                                    </div>
                                </div>

                                {/* New Password */}
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                                        New Password
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showPwFields ? 'text' : 'password'}
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            required
                                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 transition duration-200 pr-10"
                                            placeholder="Enter new password (min. 8 characters)"
                                        />
                                    </div>
                                    <PasswordStrengthChecker password={newPassword} />
                                </div>

                                {/* Confirm New Password */}
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                                        Confirm New Password
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showPwFields ? 'text' : 'password'}
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            required
                                            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 transition duration-200 pr-10"
                                            placeholder="Confirm new password"
                                        />
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    disabled={pwLoading || !checkPasswordStrength(newPassword).isValid}
                                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
                                >
                                    {pwLoading ? 'Changing Password...' : 'Change Password'}
                                </button>
                            </form>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium text-sm transition-colors shadow-sm"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ProfileModal;
